import React, { useState } from "react";
import TextInput from "../common/TextInput";
import { BeatLoader } from "react-spinners";
import { useTranslation } from "react-i18next";
import tailwindClassnames from "../common/TailwindClassnames";
import { SensorCreateDto } from "../common/ObjectTypes";
import DateSelector from "../common/DateSelector";
import SelectInput from "../common/SelectInput";
import DecommissionSensorConfirmationModal from "./DecommissionSensorConfirmationModal";
import ConfirmationModal from "../common/ConfirmationModal";
import {
  formatDateAsLocalString,
  parseLocalDateStringToDate,
} from "../common/helpers";

interface SensorFormProps {
  sensor: SensorCreateDto;
  sensorLoading: boolean;
  canEdit: boolean;
  onClose: () => void;
  onSave: Function;
  onSaveAndAddAnother: Function;
  errors: any;
  onChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => void;
  saving: boolean;
  existingSensor: boolean;
  onDecommissionSensorConfirmation: () => void;
  onReactiveSensorConfirmation: () => void;
  modalSaving: boolean;
  savingAndAddAnother: boolean;
  saveDisabled: boolean;
  /**
   * Opens the CSV import (AKL-813). Supplied by the parent only when the user may CREATE
   * sensors; the button is gated on this prop, not on `canEdit`, because editing
   * (EDIT_SENSORS_IN_ORG) is a different permission that merely coincides with creating for
   * today's roles. Only offered on the Add form — an existing sensor has nothing to import.
   */
  onImportFromCsv?: () => void;
}

const SensorForm: React.FC<SensorFormProps> = ({
  sensor,
  sensorLoading,
  canEdit,
  onClose,
  onSave,
  onSaveAndAddAnother,
  onChange,
  errors,
  saving,
  existingSensor,
  onDecommissionSensorConfirmation,
  onReactiveSensorConfirmation,
  modalSaving,
  savingAndAddAnother,
  saveDisabled,
  onImportFromCsv,
}) => {
  const { t } = useTranslation();
  const maxStringLength = 255;
  const maxCommentLength = 2000;
  const [displayConfirmationModal, setDisplayDecommissionConfirmationModal] =
    useState(false);
  const [
    displayReactiveConfirmationModal,
    setDisplayReactiveConfirmationModal,
  ] = useState(false);
  // TODO: In the future, we will support other sensor types
  const sensorTypes = [
    {
      value: "camera",
      label: t("Camera"),
    },
  ];

  const setPurchasePricePrecision = (e: any) => {
    if (e.target.value) {
      onChange({
        target: {
          name: "purchase_price",
          value: Number(e.target.value).toFixed(2).toString(),
        },
      } as any);
    }
  };

  const defaultSensorType =
    sensorTypes.find(
      (sensorType: any) => sensorType.value === sensor.sensor_type,
    ) || null;

  /**
   * Sets the date to be a string in the format YYYY-MM-DD.
   * @param name the name of the date field
   * @param date the date to be set
   */
  const handleDateChange = (e: any) => {
    onChange({
      target: {
        name: e?.target?.name,
        value: formatDateAsLocalString(e?.target?.value),
      },
    } as any);
  };

  return (
    <div className={`${tailwindClassnames.modalOverlay} z-50`}>
      <div className={`${tailwindClassnames.modalPanel} max-w-3xl`}>
        <DecommissionSensorConfirmationModal
          isOpen={displayConfirmationModal}
          confirmationMessage={
            t("Decommissioning sensor") +
            ` '${sensor.sensor_name}' ` +
            t(
              "will remove it from the list of available sensors. Do you want to proceed?",
            )
          }
          onConfirm={onDecommissionSensorConfirmation}
          onCancel={() => setDisplayDecommissionConfirmationModal(false)}
          confirmLabel={t("Decommission")}
          decommission_reason={sensor.decommission_reason}
          onDecommissionReasonChange={onChange}
          saving={modalSaving}
        />
        <ConfirmationModal
          isOpen={displayReactiveConfirmationModal}
          confirmationMessage={
            t("Are you sure you want to reactivate sensor") +
            ` '${sensor.sensor_name}'` +
            t("?")
          }
          onConfirm={onReactiveSensorConfirmation}
          onCancel={() => setDisplayReactiveConfirmationModal(false)}
          confirmLabel={t("Reactivate")}
          saving={modalSaving}
        />
        <div className={tailwindClassnames.modalHeaderRow}>
          <div className="flex items-center gap-3">
            <h2 className={tailwindClassnames.modalTitle}>
              {canEdit
                ? existingSensor
                  ? t("Update Sensor")
                  : t("Add Sensor")
                : t("Sensor Details")}
            </h2>
          </div>
          <button
            type="button"
            className={tailwindClassnames.modalCloseButton}
            onClick={onClose}
            aria-label={t("Close")}
          >
            &times;
          </button>
        </div>

        {sensorLoading ? (
          <div className={tailwindClassnames.modalBody}>
            <BeatLoader
              color={tailwindClassnames.skyBlue}
              loading={true}
              size={10}
              margin={5}
            />
          </div>
        ) : (
          <div className={tailwindClassnames.modalBody}>
            <div className="grid grid-cols-2 gap-3">
              {!sensor.is_active && (
                <div className="col-span-2">
                  <div className={`${tailwindClassnames.warningLabel} mb-3`}>
                    {t("DECOMMISSIONED")}
                  </div>
                  <TextInput
                    name="decommission_reason"
                    label={t("Decommission Reason")}
                    placeholder={t("Describe reason for decommission")}
                    value={
                      sensor.decommission_reason
                        ? sensor.decommission_reason
                        : ""
                    }
                    disabled={!canEdit}
                    className={
                      canEdit
                        ? tailwindClassnames.detailsPageTextInput
                        : tailwindClassnames.detailsPageTextInputDisabled
                    }
                    limit={2000}
                    type="text"
                    rows={3}
                    onChange={onChange}
                  />
                </div>
              )}
              <TextInput
                name="sensor_name"
                label={`${t("Name")}*`}
                placeholder=""
                value={sensor.sensor_name || ""}
                disabled={!canEdit}
                className={
                  canEdit
                    ? tailwindClassnames.detailsPageTextInput
                    : tailwindClassnames.detailsPageTextInputDisabled
                }
                limit={maxStringLength}
                type="text"
                onChange={onChange}
                error={errors.sensor_name}
              />
              <SelectInput
                options={sensorTypes}
                defaultValue={defaultSensorType}
                className={"w-full"}
                isDisabled={!canEdit}
                errors={errors.sensor_type}
                isLoading={saving || savingAndAddAnother}
                isClearable={true}
                isSearchable={true}
                isMulti={false}
                onChange={(e: any) => {
                  onChange({
                    target: {
                      name: "sensor_type",
                      value: e?.value || null,
                    },
                  } as any);
                }}
                name={t("Sensor Type") + `*`}
                searchBar={false}
              />
              <TextInput
                name="make"
                label={t("Make")}
                placeholder=""
                value={sensor.make || ""}
                disabled={!canEdit}
                className={
                  canEdit
                    ? tailwindClassnames.detailsPageTextInput
                    : tailwindClassnames.detailsPageTextInputDisabled
                }
                limit={maxStringLength}
                type="text"
                onChange={onChange}
                error={errors.make}
              />
              <TextInput
                name="model"
                label={t("Model")}
                placeholder=""
                value={sensor.model || ""}
                disabled={!canEdit}
                className={
                  canEdit
                    ? tailwindClassnames.detailsPageTextInput
                    : tailwindClassnames.detailsPageTextInputDisabled
                }
                limit={maxStringLength}
                type="text"
                onChange={onChange}
                error={errors.model}
              />
              <TextInput
                name="serial_number"
                label={t("Serial Number")}
                placeholder=""
                value={sensor.serial_number || ""}
                disabled={!canEdit}
                className={
                  canEdit
                    ? tailwindClassnames.detailsPageTextInput
                    : tailwindClassnames.detailsPageTextInputDisabled
                }
                limit={maxStringLength}
                type="text"
                onChange={onChange}
                error={errors.serial_number}
              />
              <div className="w-full">
                <DateSelector
                  label={t("Purchase Date")}
                  name="purchase_date"
                  date={parseLocalDateStringToDate(sensor.purchase_date)}
                  onDateChange={handleDateChange}
                  disabled={!canEdit}
                />
              </div>
              <TextInput
                name="purchase_price"
                label={t("Purchase Price") + " (USD $)"}
                placeholder=""
                value={
                  sensor.purchase_price ? sensor.purchase_price.toString() : ""
                }
                disabled={!canEdit}
                className={
                  canEdit
                    ? tailwindClassnames.detailsPageTextInput
                    : tailwindClassnames.detailsPageTextInputDisabled
                }
                type="number"
                onChange={onChange}
                error={errors.purchase_price}
                onBlur={setPurchasePricePrecision}
              />
              <TextInput
                name="product_url"
                label={t("Product URL")}
                placeholder=""
                value={sensor.product_url || ""}
                disabled={!canEdit}
                className={
                  canEdit
                    ? tailwindClassnames.detailsPageTextInput
                    : tailwindClassnames.detailsPageTextInputDisabled
                }
                limit={maxStringLength}
                type="text"
                onChange={onChange}
                error={errors.product_url}
              />
            </div>
            <div className=" mt-3">
              <TextInput
                name="comments"
                label={t("Comments")}
                placeholder=""
                value={sensor.comments || ""}
                disabled={!canEdit}
                className={
                  canEdit
                    ? `${tailwindClassnames.detailsPageTextInput} w-full `
                    : `${tailwindClassnames.detailsPageTextInputDisabled} w-full `
                }
                limit={maxCommentLength}
                type="text"
                onChange={onChange}
                error={errors.comments}
                rows={3}
              />
            </div>
          </div>
        )}
        {!sensorLoading && (
          <div className={tailwindClassnames.modalFooter}>
            <button
              type="button"
              onClick={onClose}
              className={tailwindClassnames.btnSecondary}
            >
              {canEdit ? t("Cancel") : t("Close")}
            </button>
            {existingSensor &&
              canEdit &&
              (sensor.is_active ? (
                <button
                  type="button"
                  onClick={() => setDisplayDecommissionConfirmationModal(true)}
                  className={tailwindClassnames.btnDanger}
                >
                  {t("Decommission")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setDisplayReactiveConfirmationModal(true)}
                  className={tailwindClassnames.btnSecondary}
                >
                  {t("Reactivate")}
                </button>
              ))}
            {!existingSensor && onImportFromCsv && (
              <button
                type="button"
                onClick={onImportFromCsv}
                disabled={saving || savingAndAddAnother}
                className={tailwindClassnames.btnSecondary}
                data-testid="import-sensors-from-csv"
              >
                {t("Import from CSV")}
              </button>
            )}
            {canEdit && !existingSensor && (
              <button
                type="button"
                onClick={() => {
                  onSaveAndAddAnother();
                }}
                disabled={saving || savingAndAddAnother}
                className={`relative ${tailwindClassnames.btnSecondary}`}
              >
                {/* Label stays in layout while saving so the button keeps its
                    width; currentColor spinner reads on the fill in both
                    themes. */}
                <span className={savingAndAddAnother ? "invisible" : ""}>
                  {t("Save and Add Another")}
                </span>
                {savingAndAddAnother && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <BeatLoader
                      color="currentColor"
                      loading={true}
                      size={10}
                      margin={5}
                    />
                  </span>
                )}
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={() => onSave()}
                disabled={saveDisabled || saving || savingAndAddAnother}
                className={`relative ${tailwindClassnames.btnPrimary}`}
              >
                <span className={saving ? "invisible" : ""}>{t("Save")}</span>
                {saving && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <BeatLoader
                      color="currentColor"
                      loading={true}
                      size={10}
                      margin={5}
                    />
                  </span>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SensorForm;
